import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as Form from '../form'
import { Platform } from '@acre-rewards/lib'
import { useTokenStore } from '@/src/common/store/useTokenStore'

export const EmptyCardApplication = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  primaryAddress: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
  },
  isMailingAddressDifferent: false,
  ssn: '',
  totalAnnualIncome: '',
  monthlyHousingPayment: '',
  acceptCardholderAgreement: false,
  offerCode: undefined,
  dob: undefined,
} satisfies Form.CardApplicationForm

interface UseCardApplicationFormStore {
  cardApplication:
    | {
        id: string
        platform: Platform
      }
    | undefined
  setCardApplication: (cardApplication: { id: string; platform: Platform }) => void
  cardApplicationForm: Form.CardApplicationForm
  clearAll: () => void
  updateField: <K extends keyof Form.CardApplicationForm>(
    field: K,
    value: Form.CardApplicationForm[K],
  ) => void
}

export const useCardApplicationFormStore = create(
  persist<UseCardApplicationFormStore>(
    (set, get) => ({
      cardApplication: undefined,
      setCardApplication: (cardApplication: { id: string; platform: Platform }) => {
        const { cardApplication: currentCardApplication } = get()

        if (currentCardApplication?.id === cardApplication.id) {
          return
        }

        set({ cardApplication, cardApplicationForm: EmptyCardApplication })
      },
      cardApplicationForm: EmptyCardApplication,
      clearAll: () =>
        set({ cardApplication: undefined, cardApplicationForm: EmptyCardApplication }),
      updateField: (field, value) => {
        set((state) => ({
          cardApplicationForm: {
            ...state.cardApplicationForm,
            [field]: value,
          },
        }))
      },
    }),
    {
      name: 'card-application-form-storage',
      storage: createJSONStorage(() => AsyncStorage),
      onRehydrateStorage: () => (state) => {
        if (typeof state?.cardApplicationForm.dob === 'string') {
          state.cardApplicationForm.dob = new Date(state.cardApplicationForm.dob)
        }
      },
    },
  ),
)

useTokenStore.subscribe(
  (state) => state.tokens?.accessToken,
  function onTokenChanged(accessToken) {
    if (!accessToken) {
      useCardApplicationFormStore.getState().clearAll()
    }
  },
)
